/**
 * The accounts list controls above the table: the Clients / Prospects / Former / All tabs, the
 * search (name, FileMaker ID, city, former names, Pipedrive ID, contact names), the "My
 * accounts" and "Needs info" toggles, and a chip per active filter (column filters included)
 * so it's always visible what the list is showing. Every value lives in the URL.
 */
import { Search, X } from "lucide-react";
import {
  useGetList,
  useListContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toggle } from "@/components/ui/toggle";
import { cn } from "@/lib/utils";

import type {
  AccountView,
  ZcrmAccount,
} from "../providers/zinspector/accounts";
import { describeFilter } from "./describeFilter";

const VIEWS: { id: AccountView | "all"; label: string }[] = [
  { id: "clients", label: "Clients" },
  { id: "prospects", label: "Prospects" },
  { id: "former", label: "Former" },
  { id: "all", label: "All" },
];

const SEARCH_FILTER = "search_text_q";
const SEARCH_DEBOUNCE_MS = 300;
const ALL_ACCOUNTS = { page: 1, perPage: 10_000 };

/** Every account, once: the tab counts and the market list come from it (the provider caches it). */
const useAllAccounts = () =>
  useGetList<ZcrmAccount>("companies", {
    pagination: ALL_ACCOUNTS,
    sort: { field: "name", order: "ASC" },
  }).data ?? [];

export const AccountViewTabs = () => {
  const translate = useTranslate();
  const { filterValues, setFilters } = useListContext();
  const accounts = useAllAccounts();
  const current = (filterValues.view as AccountView | undefined) ?? "all";

  const select = (view: AccountView | "all") => {
    const { view: _view, ...rest } = filterValues;
    setFilters(view === "all" ? rest : { ...rest, view });
  };

  return (
    <div role="tablist" className="flex gap-1 border-b">
      {VIEWS.map(({ id, label }) => {
        const count =
          id === "all"
            ? accounts.length
            : accounts.filter((a) => a.view === id).length;
        const isActive = current === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => select(id)}
            className={cn(
              "px-3 py-2 text-sm -mb-px border-b-2 transition-colors",
              isActive
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {translate(`zcrm.accounts.views.${id}`, { _: label })}
            <span className="ml-1.5 text-xs text-muted-foreground tabular-nums">
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export const AccountListFilters = ({
  identityId,
}: {
  identityId?: Identifier;
}) => {
  const translate = useTranslate();
  const { filterValues, setFilters } = useListContext();

  const setFilter = (key: string, value: unknown) => {
    const { [key]: _old, ...rest } = filterValues;
    setFilters(
      value === undefined || value === "" ? rest : { ...rest, [key]: value },
    );
  };
  const chips = Object.entries(filterValues).filter(
    ([key]) => key !== "view" && key !== SEARCH_FILTER,
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox
          value={(filterValues[SEARCH_FILTER] as string) ?? ""}
          onChange={(value) => setFilter(SEARCH_FILTER, value)}
          placeholder={translate("zcrm.accounts.search", {
            _: "Name, FileMaker ID, city, contact…",
          })}
        />
        {identityId != null && (
          <Toggle
            variant="outline"
            size="sm"
            pressed={filterValues.account_manager_id === identityId}
            onPressedChange={(pressed) =>
              setFilter("account_manager_id", pressed ? identityId : undefined)
            }
          >
            {translate("zcrm.accounts.mine", { _: "My accounts" })}
          </Toggle>
        )}
        <Toggle
          variant="outline"
          size="sm"
          pressed={filterValues.needs_info === true}
          onPressedChange={(pressed) =>
            setFilter("needs_info", pressed ? true : undefined)
          }
        >
          {translate("zcrm.accounts.needs_info", { _: "Needs info" })}
        </Toggle>
      </div>
      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map(([key, value]) => (
            <Badge key={key} variant="neutral" className="gap-1 pr-1">
              {describeFilter(key, value, identityId)}
              <button
                type="button"
                aria-label={`Remove filter ${describeFilter(key, value, identityId)}`}
                className="rounded hover:bg-background/60"
                onClick={() => setFilter(key, undefined)}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setFilters(filterValues.view ? { view: filterValues.view } : {})
            }
          >
            {translate("zcrm.accounts.clear", { _: "Clear filters" })}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const SearchBox = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  useEffect(() => {
    if (text === value) return;
    const timer = setTimeout(() => onChange(text.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text, value, onChange]);

  return (
    <div className="relative w-72">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
      <Input
        type="search"
        aria-label={placeholder}
        placeholder={placeholder}
        value={text}
        onChange={(event) => setText(event.target.value)}
        className="pl-8 h-8"
      />
    </div>
  );
};
